<template>
  <!-- Above the mobile topbar (z-index 10001 in AppSidebar) so the panel header
       and its close button aren't buried under it on phones. -->
  <div v-if="open" class="fixed inset-0 z-[10050] flex justify-end">
    <Toast
      :show="showToast"
      :type="toastType"
      :title="toastTitle"
      :message="toastMessage"
      @close="showToast = false"
    />

    <!-- Backdrop -->
    <div class="absolute inset-0 bg-slate-950/50" @click="close"></div>

    <!-- Panel -->
    <aside
      class="relative h-full w-full max-w-full sm:w-[420px] md:w-[440px] lg:w-[480px] xl:w-[520px] bg-card border-l border-border shadow-2xl flex flex-col overflow-hidden pt-[env(safe-area-inset-top)] pb-[env(safe-area-inset-bottom)] pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]"
    >

      <!-- Header -->
      <div class="px-4 sm:px-5 py-3 sm:py-4 border-b border-border flex items-start justify-between gap-3 shrink-0">
        <div class="min-w-0">
          <div class="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{{ appliesOnSite ? 'Cover letter' : 'Quick apply' }}</div>
          <h2 class="text-base font-bold text-foreground mt-0.5 truncate">{{ job?.title || 'Cover letter' }}</h2>
          <p v-if="employerName" class="text-[11px] text-muted-foreground truncate">{{ employerName }}</p>
        </div>
        <button
          type="button"
          class="inline-flex items-center justify-center h-10 w-10 sm:h-9 sm:w-9 shrink-0 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-muted active:bg-muted transition"
          aria-label="Close"
          @click="close"
        >
          <i class="fas fa-xmark text-sm"></i>
        </button>
      </div>

      <!-- Body -->
      <div ref="bodyScrollEl" class="flex-1 min-h-0 overflow-y-auto overflow-x-hidden overscroll-contain">
        <div class="p-4 sm:p-5 space-y-4 min-h-full flex flex-col">

          <!-- Subject -->
          <div>
            <label class="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Subject</label>
            <input
              v-model="coverLetterSubject"
              type="text"
              class="w-full border border-border rounded-lg px-3 py-2 text-sm bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
              placeholder="Enter cover letter subject"
            />
          </div>

          <!-- Cover letter label + credit badge + AI settings toggle -->
          <div class="flex items-start justify-between gap-2">
            <div class="min-w-0">
              <label class="block text-[11px] font-semibold text-muted-foreground uppercase tracking-wide">Cover Letter</label>
              <p class="text-[11px] text-muted-foreground">
                {{ coverLetterDraftSource === 'ai' && coverLetterContent ? 'Generated automatically with AI — chat below to revise it.' : 'Write your own, or generate one with AI.' }}
              </p>
            </div>
            <div class="shrink-0 flex items-center gap-1.5">
              <span
                v-if="quotaBadgeText"
                class="inline-flex items-center gap-1 px-2 py-1 rounded-full border border-border bg-muted/50 text-[10px] font-medium text-muted-foreground whitespace-nowrap"
              >
                <i class="fas fa-bolt text-amber-500 text-[9px]"></i>
                {{ quotaBadgeText }}
              </span>
              <button
                type="button"
                @click="showCustomizePanel = !showCustomizePanel"
                :class="!isLoggedIn ? 'opacity-40 pointer-events-none' : ''"
                class="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                title="AI Settings"
              >
                <i class="fas fa-sliders-h text-[11px]"></i>
              </button>
            </div>
          </div>

          <!-- Inline AI customize panel -->
          <div v-if="showCustomizePanel" class="rounded-lg border border-border bg-muted/40 p-3 space-y-3">
              <!-- Mounted on open, so it re-reads anything the chat saved since. -->
              <CoverLetterPreferences />

              <!-- Instructions template picker: lets the user pick a different saved
                   template just for this one application, without changing their
                   saved default (activePreset) on the AI Instructions page. -->
              <div v-if="aiInstructionsCustomPresets.length">
                <label class="block text-[10px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">Instructions</label>
                <select
                  v-model="selectedTemplateIndex"
                  data-test="instructions-template-picker"
                  class="w-full border border-border rounded-lg px-2.5 py-1.5 text-xs bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                >
                  <option v-for="(preset, idx) in aiInstructionsCustomPresets" :key="idx" :value="idx">
                    {{ preset.title || `Template ${idx + 1}` }}
                  </option>
                </select>
              </div>
              <p v-else class="text-xs text-muted-foreground" data-test="no-instructions-templates">
                You don't have any saved AI Instructions templates yet.
              </p>

              <p class="text-[11px] text-muted-foreground">
                Still not satisfied with your AI Cover Letter?
                <router-link to="/ai-instructions" class="text-primary hover:underline font-medium">Edit in AI Instructions</router-link>
              </p>
            </div>

          <!-- AI upgrade hints -->
          <p
            v-if="aiLimitNotice"
            role="alert"
            class="text-xs text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2"
            >
            {{ aiLimitNotice.message }}
            <router-link v-if="aiLimitNotice.upgradeUrl" :to="aiLimitNotice.upgradeUrl" class="text-primary hover:underline font-medium">Upgrade</router-link>
            </p>

          <p
            v-else-if="showAiCoverLetterUpgradeHint"
            class="text-xs text-muted-foreground"
          >
            {{ expiredPlanLabel }} expired. To continue generating with AI,
            <router-link to="/subscription/plans" class="text-primary hover:underline font-medium">{{ expiredPlanLabel === 'Free plan' ? 'subscribe to Basic or Premium' : 'renew your plan' }}</router-link>
            — or write your own below.
          </p>

          <p
            v-else-if="isFreeTrialAiLimited && trialAiQuotaRemaining === 0 && trialAiQuotaLimit != null"
            class="text-xs text-amber-800 dark:text-amber-200 bg-amber-500/10 border border-amber-500/25 rounded-lg px-3 py-2"
            >
            You've used all {{ trialAiQuotaLimit }} AI cover letters {{ trialAiQuotaPeriod === 'lifetime' ? 'on your plan' : 'this month' }}.
            <router-link v-if="expiredPlanLabel !== 'Premium plan'" to="/subscription/plans" class="text-primary hover:underline font-medium">Upgrade</router-link>
            <template v-if="expiredPlanLabel !== 'Premium plan'">{{ ' ' }}for more, or</template>
            <template v-else>You can</template>
            write your own cover letter below and apply as usual.
            </p>

          <!-- Loading the panel (quota, resume, saved conversation) before any draft starts -->
          <div
            v-if="preparingCoverLetter && !rewriteMessages.length"
            class="rounded-lg border border-border bg-muted/30 p-6 min-h-[160px] flex flex-col items-center justify-center gap-2 text-xs text-muted-foreground"
          >
            <i class="fas fa-spinner fa-spin text-base text-primary"></i>
            {{ checkingJob ? "Reading what this job asks for…" : "Loading…" }}
          </div>

          <!-- AI chat: every draft and every edit instruction, newest at the bottom -->
          <div
            v-else-if="showAiChat"
            class="flex-1 flex flex-col rounded-xl bg-muted/20 overflow-hidden"
          >
            <!-- No inner scrollbar: the letter shows at full height and the panel body scrolls. -->
            <div class="flex-1 p-3 space-y-3">
              <div
                v-for="msg in rewriteMessages"
                :key="msg.id"
                class="flex"
                :class="msg.role === 'user' ? 'justify-end' : 'justify-start'"
              >
                <div
                  class="rounded-2xl px-3 py-2 text-sm"
                  :class="[
                    isEditingOutput && msg.id === latestOutputMessageId ? 'w-full' : 'max-w-[90%]',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground rounded-br-sm'
                      : (msg.isError
                          ? 'bg-red-500/10 border border-red-500/20 text-red-700 dark:text-red-300 rounded-bl-sm'
                          : 'bg-card border border-border text-foreground rounded-bl-sm shadow-sm'),
                  ]"
                >
                  <textarea
                    v-if="isEditingOutput && msg.id === latestOutputMessageId"
                    ref="editTextarea"
                    v-model="editDraft"
                    rows="10"
                    class="block w-full text-sm leading-relaxed bg-background text-foreground border border-border rounded-lg p-2 resize-none overflow-hidden focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none"
                    @input="autoResizeEditTextarea"
                  ></textarea>
                  <p v-else class="whitespace-pre-wrap leading-relaxed">{{ msg.text }}</p>

                  <div v-if="msg.role === 'assistant' && !msg.isError && !msg.isNote" class="flex items-center justify-end gap-1 mt-1.5">
                    <template v-if="isEditingOutput && msg.id === latestOutputMessageId">
                      <button
                        type="button"
                        @click="cancelEditingOutput"
                        class="text-[11px] text-muted-foreground hover:text-foreground px-2 py-0.5 rounded transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        @click="saveEditingOutput"
                        class="text-[11px] font-semibold text-primary hover:underline px-2 py-0.5 rounded"
                      >
                        Save
                      </button>
                    </template>
                    <template v-else>
                      <button
                        type="button"
                        @click="copyMessageText(msg)"
                        class="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                        title="Copy"
                      >
                        <i :class="copiedMessageId === msg.id ? 'fas fa-check text-green-500' : 'fas fa-copy'" class="text-[11px]"></i>
                      </button>
                      <button
                        v-if="msg.id === latestOutputMessageId"
                        type="button"
                        @click="startEditingOutput"
                        class="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors"
                        title="Edit"
                      >
                        <i class="fas fa-pen text-[11px]"></i>
                      </button>
                      <button
                        v-if="msg.id === latestOutputMessageId"
                        type="button"
                        @click="regenerateCoverLetter"
                        :disabled="generatingCoverLetter || rewritingCoverLetter || !canGenerateAiCoverLetterNow"
                        class="inline-flex items-center justify-center h-6 w-6 rounded text-muted-foreground hover:text-primary hover:bg-muted transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:text-muted-foreground disabled:hover:bg-transparent"
                        :title="canGenerateAiCoverLetterNow ? 'Regenerate (uses 1 AI credit)' : 'No AI credits left'"
                        data-test="regenerate-cover-letter"
                      >
                        <i class="fas fa-rotate-right text-[11px]"></i>
                      </button>
                    </template>
                  </div>
                </div>
              </div>

              <!-- What the job asks for that the profile doesn't have yet - asked
                   before a draft (the first one, or a regenerate with new questions) -->
              <LetterQuestions
                v-if="letterQuestions && !generatingCoverLetter"
                :job-id="jobId"
                :questions="letterQuestions"
                @done="onLetterQuestionsDone"
              />

              <!-- Typing indicator while a draft or revision is in flight -->
              <div v-if="rewritingCoverLetter || generatingCoverLetter" class="flex justify-start">
                <div class="rounded-2xl rounded-bl-sm bg-card border border-border px-3 py-2 text-xs text-muted-foreground flex items-center gap-1.5 shadow-sm">
                  <i class="fas fa-spinner fa-spin text-primary"></i> {{ generatingCoverLetter ? 'Writing…' : 'Revising…' }}
                </div>
              </div>

              <!-- No letter yet (first generate failed, or none was attempted) -->
              <div
                v-if="!latestOutputMessageId && !generatingCoverLetter && !letterQuestions"
                class="flex flex-col items-center justify-center gap-2 py-6 text-center text-xs text-muted-foreground"
                data-test="chat-empty-state"
              >
                <p>No cover letter yet for this job.</p>
                <div class="flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    @click="generateCoverLetter()"
                    :disabled="!canGenerateAiCoverLetterNow"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-xs font-medium hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <i class="fas fa-wand-magic-sparkles text-[10px]"></i>
                    Generate cover letter
                  </button>
                  <button
                    type="button"
                    @click="writeOwnCoverLetter"
                    class="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-border bg-card text-foreground text-xs font-medium hover:border-muted-foreground/40 transition-all"
                  >
                    <i class="fas fa-pen text-[10px]"></i>
                    Write my own
                  </button>
                </div>
                <p>Or tell the AI what to focus on below.</p>
              </div>
            </div>

            <!-- Composer -->
            <div class="border-t border-border p-2.5 bg-card">
              <p
                v-if="rememberedDetails"
                class="mb-2 text-[11px] text-muted-foreground"
                data-test="chat-remembered"
              >
                <i class="fas fa-bookmark text-primary mr-1"></i>
                Saved for all your letters: {{ rememberedDetails }}.
                <button type="button" class="text-primary hover:underline font-medium" @click="showCustomizePanel = true">Edit</button>
              </p>
              <template v-if="latestOutputMessageId">
                <p class="mb-1.5 text-[11px] text-muted-foreground">
                  Tell the AI what to change, or ask it anything. Edits use 1 AI credit; answers are free.
                </p>
                <div class="mb-2 flex flex-wrap gap-1.5" data-test="chat-chips">
                  <button
                    v-for="chip in CHAT_CHIPS"
                    :key="chip"
                    type="button"
                    @click="sendChatChip(chip)"
                    :disabled="rewritingCoverLetter || generatingCoverLetter || !canGenerateAiCoverLetterNow"
                    class="px-2.5 py-1 rounded-full border border-border bg-background text-[11px] font-medium text-muted-foreground hover:border-primary/40 hover:text-primary disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {{ chip }}
                  </button>
                </div>
              </template>
              <div class="relative">
                <textarea
                  v-model="rewriteInput"
                  rows="4"
                  :placeholder="latestOutputMessageId ? 'e.g. make it shorter, add my resume link' : 'What should the AI focus on? e.g. my Shopify experience'"
                  :disabled="rewritingCoverLetter || generatingCoverLetter"
                  class="block w-full min-h-[96px] border border-border rounded-lg pl-2.5 pr-14 py-2 text-xs resize-none bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none disabled:opacity-60"
                  @keydown.enter.exact.prevent="sendRewriteInstruction"
                ></textarea>
                <button
                  type="button"
                  @click="sendRewriteInstruction"
                  :disabled="rewritingCoverLetter || generatingCoverLetter || !rewriteInput.trim() || !canGenerateAiCoverLetterNow"
                  class="absolute right-3 bottom-3 inline-flex items-center justify-center h-10 w-10 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40 disabled:cursor-not-allowed transition-all shadow-sm"
                  title="Send"
                >
                  <i v-if="rewritingCoverLetter" class="fas fa-spinner fa-spin text-sm"></i>
                  <i v-else class="fas fa-paper-plane text-sm"></i>
                </button>
              </div>
            </div>
          </div>

          <!-- Manual fallback: no AI access, quota exhausted, OLJ not connected yet, or the user chose to write their own -->
          <div v-else class="relative">
            <textarea
              ref="coverLetterTextarea"
              v-model="coverLetterContent"
              rows="9"
              placeholder="Write your own cover letter here…"
              class="w-full min-h-[160px] border border-border rounded-lg p-3 text-sm resize-none overflow-hidden bg-background text-foreground focus:ring-2 focus:ring-primary/30 focus:border-primary outline-none leading-relaxed"
              @input="autoResizeCoverLetterTextarea"
              @focus="onCoverLetterFocus"
            ></textarea>
            <button
              v-if="manualMode"
              type="button"
              @click="manualMode = false"
              class="mt-1 text-[11px] font-medium text-primary hover:underline"
            >
              <i class="fas fa-wand-magic-sparkles text-[10px] mr-1"></i>Use AI chat instead
            </button>
          </div>

          <!-- Placeholder fill-in fields (opt-in, e.g. Job Details) -->
          <div
            v-if="placeholderHelper && placeholderKeys.length"
            class="p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg"
            data-test="placeholder-helper"
          >
            <div class="flex items-start gap-2 mb-3">
              <i class="fas fa-exclamation-triangle text-amber-500 mt-0.5 shrink-0 text-sm"></i>
              <p class="text-xs font-semibold text-amber-800 dark:text-amber-200">
                Fill in {{ placeholderKeys.length === 1 ? 'this detail' : 'these details' }} — auto-replaces the bracketed text in your cover letter:
              </p>
            </div>
            <div class="space-y-2">
              <div
                v-for="key in placeholderKeys"
                :key="key"
                class="flex items-center gap-2"
              >
                <span class="text-xs font-mono text-orange-700 dark:text-orange-300 bg-orange-500/15 border border-orange-500/30 px-2 py-0.5 rounded shrink-0 max-w-[45%] truncate">
                  [{{ key }}]
                </span>
                <input
                  :value="placeholderAnswers[key] || ''"
                  type="text"
                  :placeholder="`Enter ${key.toLowerCase()}`"
                  data-test="placeholder-input"
                  class="flex-1 min-w-0 px-2 py-1 text-xs border border-border rounded focus:ring-1 focus:ring-primary focus:border-primary bg-background text-foreground"
                  @input="onPlaceholderTyped(key, $event.target.value)"
                />
                <button
                  type="button"
                  @click="suggestPlaceholder(key)"
                  :disabled="suggestingPlaceholder[key]"
                  class="shrink-0 flex items-center justify-center w-6 h-6 rounded bg-primary/10 hover:bg-primary/20 text-primary disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  title="Let AI suggest an answer"
                >
                  <i v-if="suggestingPlaceholder[key]" class="fas fa-spinner fa-spin text-xs"></i>
                  <i v-else class="fas fa-magic text-xs"></i>
                </button>
              </div>
            </div>
            <p class="text-xs text-muted-foreground mt-2">
              <i class="fas fa-save mr-1"></i>Saved for next time
            </p>
          </div>
        </div>
      </div>

      <!-- Footer actions -->
      <div class="border-t border-border p-4 sm:p-5 space-y-3 shrink-0 bg-card">

        <!-- OLJ apply points selector + balance (OLJ jobs only) -->
        <div v-if="showApplyPoints" class="flex flex-wrap items-center gap-x-2 gap-y-1">
          <span class="text-xs text-muted-foreground shrink-0">Apply Points:</span>
          <input
            v-model.number="selectedApplyPoints"
            type="number"
            min="0"
            class="w-16 border border-border rounded-md px-2 py-1 text-xs bg-background text-foreground focus:ring-1 focus:ring-primary focus:border-primary outline-none"
          />
          <span v-if="oljApplyPointsLoading" class="text-[11px] text-muted-foreground ml-auto">
            <i class="fas fa-spinner fa-spin mr-0.5"></i>Loading…
          </span>
          <span v-else-if="oljApplyPoints != null" class="text-[11px] text-muted-foreground ml-auto">
            Bal: <span class="font-semibold text-foreground">{{ oljApplyPoints }}</span>
          </span>
          <span v-else-if="oljApplyPointsError" class="text-[11px] text-red-500 ml-auto">Unavailable</span>
          <p v-if="missingApplyPoints" class="basis-full text-[11px] text-amber-600 dark:text-amber-400" data-test="apply-points-required">
            Enter at least 1 apply point to submit.
          </p>
        </div>

        <!-- OLJ jobs, saved login rejected by OnlineJobs.ph -->
        <template v-if="oljReconnectRequired">
          <div
            class="flex items-start gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200"
            data-test="olj-reconnect-warning"
          >
            <i class="fas fa-exclamation-triangle mt-0.5 text-amber-600 dark:text-amber-400"></i>
            <span>Your OnlineJobs.ph account needs to be reconnected. Applications and apply points won't work until you reconnect.</span>
          </div>
          <div class="flex flex-col sm:flex-row gap-2">
            <router-link
              :to="OLJ_RECONNECT_ROUTE"
              class="w-full sm:flex-[4] inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 text-white px-4 py-2.5 text-sm font-semibold hover:bg-amber-600 transition-colors text-center"
              data-test="olj-reconnect-button"
            >
              <i class="fas fa-plug text-xs"></i>
              <span class="truncate">Reconnect OnlineJobs.ph to Apply</span>
            </router-link>
            <button
              type="button"
              @click="viewJobPost"
              title="View Job Post"
              class="w-full sm:w-auto sm:flex-1 inline-flex items-center justify-center gap-2 rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors py-2.5 sm:py-0"
            >
              <i class="fas fa-external-link-alt text-xs"></i>
              <span class="sm:hidden">View Job Post</span>
            </button>
          </div>
        </template>

        <!-- Applied on the job's own site (every site but a connected OnlineJobs.ph) -->
        <div v-else-if="appliesOnSite" data-test="open-job-post" class="space-y-2">
          <!-- OnlineJobs.ph/Upwork/JobStreet: the extension does this on the job page itself -->
          <div
            v-if="showExtensionNudge"
            class="flex items-start gap-2.5 rounded-lg border border-border bg-muted/40 px-3 py-2.5"
            data-test="extension-nudge"
          >
            <img src="/logo/LogoIcon.png" alt="" class="h-7 w-7 rounded-md shrink-0 mt-0.5" />
            <div class="min-w-0 flex-1">
              <p class="text-xs leading-relaxed text-foreground">
                Apply faster on {{ externalPlatformLabel }}: the Applypa extension writes your {{ externalPlatformLabel === 'Upwork' ? 'proposal' : 'cover letter' }} on the job page and fills the form.
              </p>
              <a
                :href="extensionStoreUrl"
                target="_blank"
                rel="noopener"
                class="mt-1 inline-flex items-center gap-1 text-xs font-semibold text-foreground underline-offset-2 hover:underline"
              >
                Add to Chrome, it's free <i class="fas fa-arrow-up-right-from-square text-[10px]"></i>
              </a>
            </div>
            <button
              type="button"
              class="h-6 w-6 shrink-0 rounded text-muted-foreground hover:text-foreground hover:bg-muted transition"
              aria-label="Hide"
              @click="dismissExtensionNudge"
            >
              <i class="fas fa-xmark text-xs"></i>
            </button>
          </div>
          <button
            type="button"
            @click="openJobOnPlatform"
            :disabled="!job?.link || generatingCoverLetter"
            class="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <i class="fas fa-arrow-up-right-from-square text-xs"></i>
            {{ externalPlatformLabel === 'the job post' ? 'Open job post' : `Apply on ${externalPlatformLabel}` }}
          </button>
        </div>

        <!-- OLJ jobs: Submit -->
        <div v-else class="flex gap-2" data-test="submit-application">
          <button
            type="button"
            @click="submitApplication"
            :disabled="isSubmitting || generatingCoverLetter || missingApplyPoints"
            class="flex-[4] min-w-0 inline-flex items-center justify-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2.5 text-sm font-semibold hover:bg-primary/90 shadow-sm disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
          >
            <i v-if="isSubmitting" class="fas fa-spinner fa-spin text-xs"></i>
            <i v-else class="fas fa-paper-plane text-xs"></i>
            {{ isSubmitting ? 'Submitting...' : 'Submit' }}
          </button>
          <button
            type="button"
            @click="viewJobPost"
            title="View Job Post"
            class="flex-1 shrink-0 inline-flex items-center justify-center rounded-lg border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
          >
            <i class="fas fa-external-link-alt text-xs"></i>
          </button>
        </div>

        <p v-if="appliesOnSite" class="text-[11px] text-muted-foreground text-center">
          Your cover letter is copied when you open the job, so you can paste it on {{ externalPlatformLabel }}.
        </p>
      </div>
    </aside>
  </div>
</template>

<script>
import { ref, computed, watch, nextTick } from "vue";
import { apiClient } from "@/services/api";
import { OLJ_RECONNECT_ROUTE, isOljNeedsReconnect, isOljReconnectError } from "@/utils/oljConnection";
import authStore from "@/store/auth";
import { parseAiLimitError } from "@/utils/aiLimitError";
import aiInstructionsService from "@/services/aiInstructions";
import { buildCoverLetterPrompt, resolveActivePresetText } from "@/utils/coverLetterPromptBuilder";
import { useUpgradeModal } from "@/composables/useUpgradeModal";
import { useCoverLetterPlaceholders } from "@/composables/useCoverLetterPlaceholders";
import Toast from "@/components/Toast.vue";
import CoverLetterPreferences from "@/components/CoverLetterPreferences.vue";
import LetterQuestions from "@/components/LetterQuestions.vue";
import jobPreflight from "@/services/jobPreflight";
import {
  EXTENSION_STORE_URL,
  canInstallExtensionHere,
  extensionStatus,
  hideNudgeThisSession,
  nudgeHiddenThisSession,
} from "@/composables/useExtensionPrompt";

// One-tap edits shown above the chat box. Sent as-is as the rewrite instruction.
const CHAT_CHIPS = [
  "Make it shorter",
  "Sound more natural",
  "Add my resume link",
  "Add my rate & availability",
];

// { "Hourly rate": "$9/hr", ... } -> "Hourly rate $9/hr · ..."
// Details (rate, resume link...), Preferences (length, greeting...) and a story
// about their work ("I built...") the chat just saved.
const formatRememberedDetails = (remembered, savedPreferences, savedStory) =>
  [
    ...Object.entries(remembered || {}).map(([key, value]) => `${key} ${value}`),
    ...(savedPreferences || []).map((item) => `${item.label}: ${item.value}`),
    ...(savedStory ? [`"${savedStory}"`] : []),
  ].join(" · ");

export default {
  name: "QuickApplySidebar",

  components: { Toast, CoverLetterPreferences, LetterQuestions },

  props: {
    open: { type: Boolean, default: false },
    /** Full job detail object (falls back to the list row when details aren't loaded). */
    job: { type: Object, default: null },
    /** Show inputs for the [BRACKETED] blanks the AI leaves in a letter. */
    placeholderHelper: { type: Boolean, default: false },
    /** Let the parent show its own "cover letter required" modal instead of a toast. */
    coverLetterRequiredModal: { type: Boolean, default: false },
  },

  // applied: (jobId, { coverLetterId, draftSource })
  emits: ["close", "applied", "open-external", "cover-letter-required"],

  setup(props, { emit }) {
    const { openModal } = useUpgradeModal();

    // --- Toast ---
    const showToast = ref(false);
    const toastType = ref("info");
    const toastTitle = ref("");
    const toastMessage = ref("");

    const showToastNotification = (type, title, message, duration = 3000) => {
      showToast.value = false;
      toastType.value = type;
      toastTitle.value = title;
      toastMessage.value = message;
      setTimeout(() => {
        showToast.value = true;
        setTimeout(() => {
          showToast.value = false;
        }, duration);
      }, 50);
    };

    // --- Cover letter state ---
    const coverLetterSubject = ref("");
    const coverLetterContent = ref("");
    const coverLetterBase = ref("");
    const coverLetterDraftSource = ref("manual");
    const coverLetterTextarea = ref(null);
    const generatingCoverLetter = ref(false);
    // Set when the AI endpoint answers 429 (out of credits / hourly rate limit).
    const aiLimitNotice = ref(null);
    const isSubmitting = ref(false);

    // --- Rewrite with AI (chat) ---
    // Full transcript: every generated draft and every edit instruction, newest
    // last - the AI output bubbles double as the version history, Claude-style.
    const rewriteMessages = ref([]);
    const rewriteInput = ref("");
    const rewritingCoverLetter = ref(false);
    // Details the last chat message saved to the profile, e.g. "Hourly rate $9/hr".
    const rememberedDetails = ref("");
    const bodyScrollEl = ref(null);
    const isEditingOutput = ref(false);
    const editDraft = ref("");
    const copiedMessageId = ref(null);
    // Set by "Write my own" - swaps the chat for the plain editor.
    const manualMode = ref(false);
    // Questions for what this job asks for that the profile doesn't have yet,
    // shown before the first draft (null = none to ask).
    const letterQuestions = ref(null);
    let messageIdCounter = 0;

    const scrollChatToBottom = async () => {
      await nextTick();
      const el = bodyScrollEl.value;
      if (el) el.scrollTop = el.scrollHeight;
    };

    const pushAssistantMessage = (subject, content, { isError = false, isNote = false, coverLetterId = null } = {}) => {
      messageIdCounter += 1;
      rewriteMessages.value.push({
        id: messageIdCounter,
        role: "assistant",
        subject,
        text: content,
        isError,
        isNote,
        coverLetterId,
      });
      scrollChatToBottom();
    };

    const pushUserMessage = (text) => {
      messageIdCounter += 1;
      rewriteMessages.value.push({ id: messageIdCounter, role: "user", text });
      scrollChatToBottom();
    };

    // The newest non-error AI draft - the one "Edit" applies to and the one that
    // actually gets submitted with the application.
    const latestOutputMessageId = computed(() => {
      for (let i = rewriteMessages.value.length - 1; i >= 0; i -= 1) {
        const m = rewriteMessages.value[i];
        if (m.role === "assistant" && !m.isError && !m.isNote) return m.id;
      }
      return null;
    });

    // Writes a letter that had its blanks filled back to the draft and to the
    // newest chat bubble, so what's shown is what gets submitted.
    const setFilledContent = (text) => {
      coverLetterContent.value = text;
      const latestMsg = rewriteMessages.value.find((m) => m.id === latestOutputMessageId.value);
      if (latestMsg) latestMsg.text = text;
    };

    const placeholders = useCoverLetterPlaceholders({
      jobId: () => props.job?.id || null,
      getContent: () => coverLetterContent.value,
      setContent: setFilledContent,
    });

    /** Every new draft (generated, revised, edited or reloaded) goes through here. */
    const withPlaceholders = (text) => (props.placeholderHelper ? placeholders.prepareDraft(text) : text);

    // The edit box grows to fit the whole letter (no scrollbar of its own);
    // the panel body scrolls instead, same as the read-only bubble.
    const editTextarea = ref(null);
    const autoResizeEditTextarea = async () => {
      await nextTick();
      const target = Array.isArray(editTextarea.value) ? editTextarea.value[0] : editTextarea.value;
      if (!target) return;
      const borderPx = target.offsetHeight - target.clientHeight;
      target.style.height = "auto";
      target.style.height = `${target.scrollHeight + borderPx}px`;
    };

    const startEditingOutput = () => {
      editDraft.value = coverLetterContent.value;
      isEditingOutput.value = true;
      autoResizeEditTextarea();
    };

    const saveEditingOutput = async () => {
      const newText = withPlaceholders(editDraft.value);
      coverLetterContent.value = newText;
      const latestMsg = rewriteMessages.value.find((m) => m.id === latestOutputMessageId.value);
      if (latestMsg) latestMsg.text = newText;
      isEditingOutput.value = false;

      if (latestMsg?.coverLetterId) {
        try {
          await apiClient.patch(`/cover-letters/${latestMsg.coverLetterId}/`, { content: newText });
        } catch (e) {
          console.error("Failed to save the edited cover letter:", e);
        }
      }
    };

    const cancelEditingOutput = () => {
      isEditingOutput.value = false;
    };

    const copyMessageText = async (msg) => {
      try {
        await navigator.clipboard.writeText(msg.text || "");
        copiedMessageId.value = msg.id;
        setTimeout(() => {
          if (copiedMessageId.value === msg.id) copiedMessageId.value = null;
        }, 1500);
      } catch {
        // Clipboard blocked (permissions/non-secure context) - nothing to fall back to here.
      }
    };

    // Small persistent credit pill shown above the chat, independent of plan type.
    const quotaBadgeText = computed(() => {
      if (aiGenerateQuotaLoading.value && aiGenerateQuota.value == null) return "Loading credits…";
      const q = aiGenerateQuota.value;
      if (q == null) return null;
      if (!q.applies) return "Unlimited AI credits";
      const rem = q.remaining ?? 0;
      const lim = q.limit ?? 0;
      return `${rem}/${lim} AI credits left`;
    });

    const lastAiGeneratePromptSnapshot = ref(null);
    const lastAiSystemPromptSnapshot = ref(null);
    const lastAiUserPromptSnapshot = ref(null);

    const aiGenerateQuota = ref(null);
    const aiGenerateQuotaLoading = ref(false);

    const savedAIInstructions = ref("");
    const currentAIInstructions = ref("");

    const coverLetterBullets = ref(false);
    const coverLetterModel = ref("claude-sonnet-4-6");
    const coverLetterUseResume = ref(true);
    const coverLetterUseProfile = ref(true);

    const showCustomizePanel = ref(false);

    // --- AI Instructions templates (v2 blob) ---
    // The full list of the user's saved templates, read from their AI
    // Instructions. `selectedTemplateIndex` is the one picked just for THIS
    // application - it defaults to the user's saved active template, but
    // changing it here never writes back to the server.
    const aiInstructionsCustomPresets = ref([]);
    const aiInstructionsSavedActivePreset = ref(null);
    const selectedTemplateIndex = ref(null);
    const selectedTemplateKey = computed(() =>
      selectedTemplateIndex.value != null ? `custom-${selectedTemplateIndex.value}` : null,
    );

    const userResumes = ref([]);

    // --- OLJ state ---
    const oljConnected = ref(false);
    // OLJ rejected the saved login: applying and apply points won't work until reconnect.
    const oljNeedsReconnect = ref(false);
    const oljApplyPoints = ref(null);
    const oljApplyPointsLoading = ref(false);
    const oljApplyPointsError = ref(null);
    const selectedApplyPoints = ref(0);

    // --- Derived ---
    const jobId = computed(() => props.job?.id || null);

    const employerName = computed(() => {
      const e = props.job?.employer;
      if (!e) return props.job?.company_name || "";
      return typeof e === "string" ? e : e.name || "";
    });

    const isLoggedIn = computed(() => authStore.state.isLoggedIn);
    const oljUnlocked = computed(() => !!authStore.state.user?.olj_jobs_enabled);

    const isExternalJob = computed(() => {
      const src = (props.job?.source || "").toLowerCase();
      return src !== "olj";
    });

    const externalPlatformLabel = computed(() => {
      const src = (props.job?.source || "").toLowerCase();
      const map = {
        jobstreet: "JobStreet",
        cloudstaff: "Cloudstaff",
        emapta: "Emapta",
        bossjob: "BossJob.ph",
        kalibrr: "Kalibrr",
        findtalent: "FindTalent.ph",
        hiretalent: "HireTalent.ph",
        ofmjobs: "OFM Jobs",
        linkedin: "LinkedIn",
        indeed: "Indeed",
        upwork: "Upwork",
        google: "Google Jobs",
        olj: "OnlineJobs.ph",
      };
      return map[src] || "the job post";
    });

    // Submitting from Applypa needs a connected OnlineJobs.ph account. Every other job,
    // OnlineJobs.ph included, is applied for on its own site with the Chrome extension,
    // the same way as Upwork and JobStreet (Oct 26: new users go to the extension).
    const appliesOnSite = computed(
      () => !(!isExternalJob.value && isLoggedIn.value && oljUnlocked.value && oljConnected.value),
    );

    // "Add Applypa to Chrome" nudge on OnlineJobs.ph/Upwork/JobStreet jobs: feature flag
    // from the server, then desktop Chromium without the extension, until hidden this session.
    const EXTENSION_SOURCES = ["olj", "upwork", "jobstreet"];
    const extensionMissing = ref(false);
    const extensionNudgeHidden = ref(nudgeHiddenThisSession());
    if (canInstallExtensionHere()) {
      extensionStatus().then((status) => { extensionMissing.value = status === "missing"; });
    }
    const showExtensionNudge = computed(() =>
      authStore.state.user?.extension_prompt_enabled === true &&
      EXTENSION_SOURCES.includes((props.job?.source || "").toLowerCase()) &&
      extensionMissing.value &&
      !extensionNudgeHidden.value,
    );
    const dismissExtensionNudge = () => {
      hideNudgeThisSession();
      extensionNudgeHidden.value = true;
    };

    const canUseAICoverLetterFeatures = computed(() => {
      if (!authStore.state.isLoggedIn) return false;
      // Free plan users can generate letters up to their monthly quota.
      return true;
    });

    const isFreeTrialAiLimited = computed(() => aiGenerateQuota.value?.applies === true);
    const trialAiQuotaRemaining = computed(() =>
      aiGenerateQuota.value?.applies === true ? aiGenerateQuota.value?.remaining : null,
    );
    const trialAiQuotaLimit = computed(() =>
      aiGenerateQuota.value?.applies === true ? aiGenerateQuota.value?.limit : null,
    );
    const trialAiQuotaPeriod = computed(() => aiGenerateQuota.value?.period ?? "month");

    const canGenerateAiCoverLetterNow = computed(() => {
      if (!canUseAICoverLetterFeatures.value) return false;
      const q = aiGenerateQuota.value;
      if (q == null || !q.applies) return true;
      return (q.remaining ?? 0) > 0;
    });

    // OLJ needs 1–60 apply points per application; 0 is rejected by the apply endpoint.
    const missingApplyPoints = computed(
      () => !isExternalJob.value && !(Number(selectedApplyPoints.value) >= 1),
    );

    const oljReconnectRequired = computed(
      () =>
        isLoggedIn.value &&
        oljUnlocked.value &&
        !isExternalJob.value &&
        oljConnected.value &&
        oljNeedsReconnect.value,
    );

    /**
     * Apply points only exist for OnlineJobs.ph, and the balance needs the
     * connected account - without one, the footer sends them to apply on OnlineJobs.ph.
     */
    const showApplyPoints = computed(
      () =>
        isLoggedIn.value &&
        oljUnlocked.value &&
        !isExternalJob.value &&
        oljConnected.value &&
        !oljReconnectRequired.value,
    );

    // The chat stays up whenever AI can be used - including after a failed or
    // never-attempted first draft - so errors show in it and the user can retry
    // or steer the first draft from it. Otherwise: the plain editor.
    // Connecting OnlineJobs.ph is only needed to submit, never to write the letter.
    const showAiChat = computed(
      () =>
        !manualMode.value &&
        (rewriteMessages.value.length > 0 ||
          (canUseAICoverLetterFeatures.value && canGenerateAiCoverLetterNow.value)),
    );

    const writeOwnCoverLetter = async () => {
      manualMode.value = true;
      coverLetterDraftSource.value = "manual";
      await nextTick();
      coverLetterTextarea.value?.focus();
    };

    const showAiCoverLetterUpgradeHint = computed(
      () => authStore.state.isLoggedIn && !canUseAICoverLetterFeatures.value,
    );

    const expiredPlanLabel = computed(() => {
      const planType = authStore.state.user?.subscription?.plan?.plan_type;
      if (planType === "basic") return "Basic plan";
      if (planType === "premium") return "Premium plan";
      return "Free plan";
    });

    const generateWithAiButtonTitle = computed(() => {
      if (!authStore.state.isLoggedIn) return "Sign in to generate a cover letter with AI";
      if (!canUseAICoverLetterFeatures.value) return "Upgrade to Basic or Premium to use AI cover letters";
      const q = aiGenerateQuota.value;
      if (aiGenerateQuotaLoading.value || q == null) return "Loading AI cover letter quota…";
      if (!q.applies) return "Unlimited AI cover letters on your plan";
      const rem = q.remaining ?? 0;
      const lim = q.limit ?? 10;
      if (rem <= 0)
        return `Monthly limit reached — ${lim} AI cover letters used. Resets on the 1st of next month.`;
      return `${rem} of ${lim} AI cover letters remaining this month`;
    });

    const autoResizeCoverLetterTextarea = async () => {
      await nextTick();
      requestAnimationFrame(() => {
        const el = coverLetterTextarea.value;
        if (!el) return;
        // Textarea grows to fit the whole letter with no scrollbar of its own;
        // the panel body scrolls instead. The minimum stays small on short
        // mobile viewports.
        const viewportHeight = window.visualViewport?.height || window.innerHeight;
        const minPx = Math.min(160, Math.max(100, viewportHeight - 420));
        const borderPx = el.offsetHeight - el.clientHeight;
        const clone = el.cloneNode(true);
        clone.style.position = "absolute";
        clone.style.visibility = "hidden";
        clone.style.height = "auto";
        clone.style.width = el.offsetWidth + "px";
        clone.style.overflow = "hidden";
        document.body.appendChild(clone);
        const newHeight = Math.max(minPx, clone.scrollHeight + borderPx);
        document.body.removeChild(clone);
        el.style.height = `${newHeight}px`;
      });
    };

    // On mobile, the on-screen keyboard shrinks the viewport after focus —
    // scroll the field into view once the keyboard has finished animating in.
    const onCoverLetterFocus = () => {
      setTimeout(() => {
        coverLetterTextarea.value?.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }, 300);
    };

    // --- AI settings (shared with the Job Details panel) ---
    // Resolves the prompt text for a legacy (v1 or older) blob, or for a v2
    // blob's *saved* active template — used only as a last-resort fallback
    // (e.g. before any generation has happened this session).
    const extractPromptFromInstructions = (instructions) => {
      if (!instructions || !instructions.trim()) return "";
      try {
        const parsed = JSON.parse(instructions);
        if (typeof parsed === "string") return parsed;
        if (parsed?.version === 2 || Array.isArray(parsed?.customPresets)) {
          return resolveActivePresetText(parsed.activePreset, parsed.customPresets);
        }
        if (parsed?.instructions && parsed.selectedIndex != null) {
          return parsed.instructions?.[parsed.selectedIndex]?.text || "";
        }
        return "";
      } catch {
        return instructions;
      }
    };

    // Legacy (pre-v2) fields that are still read defensively — only
    // model/bullets/useResume/useProfile survive into the v2 blob; the rest
    // (tone/length/focusAreas/strategy/preferences) are no longer written or
    // used to build the prompt, and the Tone/Length/Focus chips they used to
    // back have been replaced by the Instructions template picker below.
    const syncInlineSettingsFromSaved = () => {
      if (!savedAIInstructions.value) return;
      try {
        const parsed = JSON.parse(savedAIInstructions.value);
        if (parsed.format) coverLetterBullets.value = parsed.format === "bullets";
        else if (typeof parsed.bullets === "boolean") coverLetterBullets.value = parsed.bullets;
        if (parsed.model) coverLetterModel.value = parsed.model;
        if (typeof parsed.useResume === "boolean") coverLetterUseResume.value = parsed.useResume;
        if (typeof parsed.useProfile === "boolean") coverLetterUseProfile.value = parsed.useProfile;

        aiInstructionsCustomPresets.value = Array.isArray(parsed.customPresets) ? parsed.customPresets : [];
        aiInstructionsSavedActivePreset.value = parsed.activePreset || null;

        // Pre-select whichever template is the user's saved default - they can
        // still pick a different one below just for this application.
        const key = aiInstructionsSavedActivePreset.value;
        if (key && String(key).startsWith("custom-")) {
          const idx = parseInt(String(key).replace("custom-", ""), 10);
          selectedTemplateIndex.value = aiInstructionsCustomPresets.value[idx] ? idx : null;
        } else if (aiInstructionsCustomPresets.value.length) {
          selectedTemplateIndex.value = 0;
        } else {
          selectedTemplateIndex.value = null;
        }
      } catch (e) {
        /* not JSON, ignore */
      }
    };

    const buildInlinePrompt = () =>
      buildCoverLetterPrompt({
        version: 2,
        activePreset: selectedTemplateKey.value,
        customPresets: aiInstructionsCustomPresets.value,
      });

    // --- Data loading ---
    const loadSavedAIInstructions = async () => {
      if (!authStore.state.isLoggedIn) return;
      try {
        const instructions = await aiInstructionsService.getSavedInstructions();
        if (instructions && instructions.trim()) {
          savedAIInstructions.value = instructions;
          syncInlineSettingsFromSaved();
          currentAIInstructions.value = extractPromptFromInstructions(instructions);
        }
      } catch (e) {
        console.error("Failed to load AI instructions:", e);
      }
    };

    const fetchAiGenerateQuota = async () => {
      if (!authStore.state.isLoggedIn) {
        aiGenerateQuota.value = null;
        return;
      }
      aiGenerateQuotaLoading.value = true;
      try {
        const res = await apiClient.get("/cover-letters/ai-generate-quota/");
        aiGenerateQuota.value = res.data || null;
      } catch (e) {
        aiGenerateQuota.value = null;
      } finally {
        aiGenerateQuotaLoading.value = false;
      }
    };

    const fetchUserResumes = async () => {
      if (!authStore.state.isLoggedIn) return;
      try {
        const res = await apiClient.get("/resumes/");
        userResumes.value = Array.isArray(res.data) ? res.data : res.data?.results || [];
      } catch (e) {
        userResumes.value = [];
      }
    };

    const fetchOljConnectionStatus = async () => {
      if (!authStore.state.isLoggedIn) {
        oljConnected.value = false;
        return;
      }
      try {
        const res = await apiClient.get("/dashboard/summary/");
        oljConnected.value = !!res.data?.olj_connected;
        oljNeedsReconnect.value = isOljNeedsReconnect(res.data?.olj_connection_status);
      } catch (err) {
        console.error("Error checking OLJ connection status:", err);
        oljConnected.value = false;
        oljNeedsReconnect.value = false;
      }
    };

    const pollTaskStatus = async (taskId, { onSuccess, onError, intervalMs = 2000, maxAttempts = 30 }) => {
      let attempts = 0;
      return new Promise((resolve) => {
        const interval = setInterval(async () => {
          attempts++;
          try {
            const res = await apiClient.get(`olj/task-status/${taskId}/`);
            const data = res.data;
            if (data.status === "success") {
              clearInterval(interval);
              onSuccess(data.result);
              resolve(data.result);
            } else if (data.status === "error") {
              clearInterval(interval);
              onError(data.error || "Task failed");
              resolve(null);
            } else if (attempts >= maxAttempts) {
              clearInterval(interval);
              onError("Timed out waiting for result");
              resolve(null);
            }
          } catch (err) {
            clearInterval(interval);
            onError(err.response?.data?.error || "Error checking task status");
            resolve(null);
          }
        }, intervalMs);
      });
    };

    const fetchOljApplyPoints = async () => {
      if (!authStore.state.isLoggedIn || props.job?.source !== "olj") return;

      oljApplyPoints.value = null;
      oljApplyPointsError.value = null;
      oljApplyPointsLoading.value = true;

      try {
        const res = await apiClient.get(`olj/apply-points/${jobId.value}`);
        if (res.data.points !== undefined) {
          oljApplyPoints.value = res.data.points;
          oljApplyPointsLoading.value = false;
        } else if (res.data.task_id) {
          await pollTaskStatus(res.data.task_id, {
            onSuccess: (result) => {
              oljApplyPoints.value = result.points ?? null;
              if (result.error) oljApplyPointsError.value = result.error;
            },
            onError: (err) => {
              oljApplyPointsError.value = err;
            },
          });
          oljApplyPointsLoading.value = false;
        } else {
          oljApplyPointsLoading.value = false;
        }
      } catch (err) {
        if (isOljReconnectError(err)) oljNeedsReconnect.value = true;
        oljApplyPointsError.value = err.response?.data?.error || "Unable to fetch apply points.";
        oljApplyPointsLoading.value = false;
      }
    };

    // --- Generate ---
    // `instruction`: what the user typed in the chat before any draft existed;
    // it is added to their saved AI settings for this first draft.
    const generateCoverLetter = async (instruction = "") => {
      if (!canUseAICoverLetterFeatures.value) {
        showToastNotification(
          "warning",
          "Upgrade required",
          "Generate with AI is available on Basic and Premium plans.",
          4000,
        );
        return;
      }

      if (!canGenerateAiCoverLetterNow.value) {
        const lim =
          aiGenerateQuota.value?.applies === true && typeof aiGenerateQuota.value?.limit === "number"
            ? aiGenerateQuota.value.limit
            : 10;
        showToastNotification(
          "warning",
          "Monthly limit reached",
          `Your plan includes ${lim} AI cover letters per month. Resets on the 1st of next month.`,
          6000,
        );
        return;
      }

      aiLimitNotice.value = null;
      letterQuestions.value = null;
      generatingCoverLetter.value = true;

      try {
        const basePrompt = buildInlinePrompt();
        const effectivePrompt = instruction
          ? [basePrompt, instruction].filter((p) => p && p.trim()).join("\n\n")
          : basePrompt;
        currentAIInstructions.value = effectivePrompt;

        let selectedOljIdForAi = null;
        try {
          const parsedAi = JSON.parse(savedAIInstructions.value || "{}");
          if (parsedAi?.selectedHistoryId != null) selectedOljIdForAi = parsedAi.selectedHistoryId;
        } catch (e) {
          selectedOljIdForAi = null;
        }

        const response = await apiClient.post(
          "/cover-letters/generate/",
          {
            job_id: jobId.value,
            resume_id: coverLetterUseResume.value ? userResumes.value?.[0]?.id || null : null,
            use_profile: coverLetterUseProfile.value,
            custom_prompt: effectivePrompt,
            bullets: coverLetterBullets.value,
            model: coverLetterModel.value,
            ...(selectedOljIdForAi != null ? { selected_olj_application_id: selectedOljIdForAi } : {}),
          },
          { timeout: 180000 },
        );

        // The letter's own subject replaces the "Application for ..." stand-in set
        // when the panel opened - it can be the exact subject the post requires.
        if (response.data.subject) {
          coverLetterSubject.value = response.data.subject;
        } else if (!coverLetterSubject.value) {
          coverLetterSubject.value = `Application for ${props.job?.title || ""}`;
        }

        coverLetterDraftSource.value = "ai";
        lastAiGeneratePromptSnapshot.value =
          typeof effectivePrompt === "string" && effectivePrompt.trim() ? effectivePrompt.trim() : null;

        const d = response.data || {};
        lastAiSystemPromptSnapshot.value =
          typeof d.ai_system_prompt === "string" && d.ai_system_prompt.trim() ? d.ai_system_prompt : null;
        lastAiUserPromptSnapshot.value =
          typeof d.ai_user_prompt === "string" && d.ai_user_prompt.trim() ? d.ai_user_prompt : null;

        const generatedContent = withPlaceholders((response.data.content || "").replace(/\*\*/g, ""));
        coverLetterBase.value = generatedContent;
        coverLetterContent.value = generatedContent;
        pushAssistantMessage(coverLetterSubject.value, generatedContent, { coverLetterId: d.id ?? null });

        await nextTick();
        autoResizeCoverLetterTextarea();
        fetchAiGenerateQuota();
        window.dispatchEvent(new CustomEvent("ai-quota-updated"));
      } catch (err) {
        console.error("Error generating cover letter:", err);
        const aiLimit = parseAiLimitError(err);
        if (aiLimit) {
          aiLimitNotice.value = aiLimit;
          if (aiLimit.code === "monthly_ai_limit") fetchAiGenerateQuota();
          return;
        }
        if (err?.response?.status === 429) {
          fetchAiGenerateQuota();
          openModal("ai-cover-letter");
          return;
        }
        const data = err?.response?.data;
        const baseMsg =
          (typeof data === "object" && data && (data.error || data.detail)) ||
          (typeof data === "string" ? data : null) ||
          err?.message ||
          "Could not generate cover letter. Try again.";
        const details = typeof data === "object" && data?.details ? ` (${data.details})` : "";
        pushAssistantMessage(null, `Couldn't generate your cover letter: ${baseMsg}${details}`, { isError: true });
      } finally {
        generatingCoverLetter.value = false;
      }
    };

    // A fresh draft from scratch, added to the chat as a new version. Goes through
    // the same generate endpoint, so it costs one AI credit like the first draft.
    const regenerateCoverLetter = async () => {
      if (generatingCoverLetter.value || rewritingCoverLetter.value) return;
      isEditingOutput.value = false;
      scrollChatToBottom();
      // A job read again (or a post edited) can have questions the first draft never
      // asked - a 10-item "How to apply" got 2 of them answered (Oct 24). Ask the new
      // ones first; questions already shown in this session aren't asked again.
      const questions = (await loadLetterQuestions()).filter((q) => !shownQuestionIds.has(q.id));
      if (questions.length) {
        questions.forEach((q) => shownQuestionIds.add(q.id));
        letterQuestions.value = questions;
        scrollChatToBottom();
        return;
      }
      await generateCoverLetter();
    };

    // --- Questions before the first draft ---
    // Only what the job asks for and the profile doesn't have; at most two,
    // each with tap-to-answer options. Never blocks writing if it fails.
    const checkingJob = ref(false);
    const shownQuestionIds = new Set();
    const loadLetterQuestions = async () => {
      checkingJob.value = true;
      try {
        const result = await jobPreflight.check(jobId.value);
        return result?.available && Array.isArray(result.gaps) ? result.gaps : [];
      } catch (e) {
        return [];
      } finally {
        checkingJob.value = false;
      }
    };

    const onLetterQuestionsDone = async () => {
      letterQuestions.value = null;
      await generateCoverLetter();
    };

    // --- Rewrite with AI (chat) ---
    const sendRewriteInstruction = async () => {
      const instruction = rewriteInput.value.trim();
      if (!instruction || rewritingCoverLetter.value || generatingCoverLetter.value) return;

      // Nothing to revise yet - the message steers the first draft instead.
      if (!latestOutputMessageId.value) {
        pushUserMessage(instruction);
        rewriteInput.value = "";
        await generateCoverLetter(instruction);
        return;
      }

      if (!canGenerateAiCoverLetterNow.value) {
        showToastNotification(
          "warning",
          "Monthly limit reached",
          "Upgrade your plan to keep revising with AI, or edit the text yourself.",
          6000,
        );
        return;
      }

      // Only past instructions go to the backend - the current draft is already
      // sent as `content`, so echoing every prior full letter back would just
      // burn tokens for no benefit.
      // The assistant's own replies go too (not its letters): without them, "Stripe's
      // website" right after "What's your favourite website?" wasn't seen as the answer.
      const historyForRequest = rewriteMessages.value
        .filter((m) => m.role === "user" || (m.role === "assistant" && m.isNote && !m.isError))
        .map((m) => ({ role: m.role, text: m.text }));
      pushUserMessage(instruction);
      rewriteInput.value = "";
      rewritingCoverLetter.value = true;
      rememberedDetails.value = "";
      aiLimitNotice.value = null;
      scrollChatToBottom();

      try {
        const response = await apiClient.post(
          "/cover-letters/rewrite/",
          {
            content: coverLetterContent.value,
            subject: coverLetterSubject.value,
            instruction,
            job_id: jobId.value,
            history: historyForRequest,
          },
          { timeout: 120000 },
        );

        const data = response.data || {};
        // The AI answered in words (it needs something, or can't do that) - no new letter.
        if (data.reply) {
          pushAssistantMessage(null, data.reply, { isNote: true });
          if (data.quota) aiGenerateQuota.value = data.quota;
          rememberedDetails.value = formatRememberedDetails(data.remembered, data.saved_preferences, data.saved_story);
          return;
        }
        const revisedContent = withPlaceholders((data.content || "").replace(/\*\*/g, ""));
        coverLetterContent.value = revisedContent;
        if (data.subject) coverLetterSubject.value = data.subject;
        coverLetterDraftSource.value = "ai";
        pushAssistantMessage(coverLetterSubject.value, revisedContent, { coverLetterId: data.id ?? null });
        if (data.quota) aiGenerateQuota.value = data.quota;
        rememberedDetails.value = formatRememberedDetails(data.remembered, data.saved_preferences, data.saved_story);

        await nextTick();
        autoResizeCoverLetterTextarea();
        fetchAiGenerateQuota();
        window.dispatchEvent(new CustomEvent("ai-quota-updated"));
      } catch (err) {
        console.error("Error rewriting cover letter:", err);
        const aiLimit = parseAiLimitError(err);
        if (aiLimit) {
          aiLimitNotice.value = aiLimit;
          if (aiLimit.code === "monthly_ai_limit") fetchAiGenerateQuota();
        } else if (err?.response?.status === 429) {
          fetchAiGenerateQuota();
          openModal("ai-cover-letter");
        } else {
          const data = err?.response?.data;
          const baseMsg =
            (typeof data === "object" && data && (data.error || data.detail)) ||
            err?.message ||
            "Could not rewrite the cover letter. Try again.";
          showToastNotification("error", "Rewrite failed", baseMsg, 6000);
        }
        pushAssistantMessage(null, "Sorry, I couldn't make that change. Please try again.", { isError: true });
      } finally {
        rewritingCoverLetter.value = false;
      }
    };

    const sendChatChip = (text) => {
      rewriteInput.value = text;
      sendRewriteInstruction();
    };

    // --- Submit ---
    const formatApplyFailureMessage = (resultLike) => {
      const errorCode = (resultLike && resultLike.error) || "";
      const apiMessage = (resultLike && resultLike.message) || "";
      if (apiMessage) return apiMessage;
      if (errorCode === "already_applied") return "You already applied to this job.";
      if (errorCode === "id_proof_too_low")
        return "Your ID Proof is lower than required to apply for this job.";
      if (typeof errorCode === "string" && errorCode.trim()) return errorCode;
      return "There was an error submitting your application.";
    };

    const finishApplySuccessUi = (successMessage, coverLetterId = null) => {
      showToastNotification(
        "success",
        "Application Submitted",
        successMessage || "Your application has been submitted successfully!",
      );
      if (props.placeholderHelper) placeholders.saveTypedToServer();
      emit("applied", jobId.value, { coverLetterId, draftSource: coverLetterDraftSource.value });
      setTimeout(() => emit("close"), 1200);
    };

    const viewJobPost = () => {
      emit("open-external", props.job);
    };

    // Applying on the job's own site: copy the cover letter first, because opening the job closes this panel.
    const openJobOnPlatform = async () => {
      const text = (coverLetterContent.value || "").trim();
      if (text) {
        try {
          await navigator.clipboard.writeText(text);
        } catch {
          // Clipboard blocked — the job still opens.
        }
      }
      emit("open-external", props.job);
    };

    const submitApplication = async () => {
      if (isSubmitting.value) return;

      // Applied on the job's own site: open the job post so the user can apply there
      // with the cover letter they just generated (same as the Job Details page).
      if (appliesOnSite.value) {
        showToastNotification(
          "info",
          `Opening ${externalPlatformLabel.value}`,
          `Opening ${externalPlatformLabel.value} in a new tab so you can apply with your cover letter.`,
        );
        emit("open-external", props.job);
        return;
      }

      if (oljNeedsReconnect.value) return; // the button is "Reconnect" instead
      if (missingApplyPoints.value) return; // Submit is disabled until points are set

      if (!coverLetterContent.value.trim()) {
        if (props.coverLetterRequiredModal) {
          emit("cover-letter-required");
        } else {
          showToastNotification(
            "warning",
            "Cover letter required",
            "Generate or write a cover letter before submitting.",
            4000,
          );
        }
        return;
      }

      if (!authStore.state.isLoggedIn) return;

      isSubmitting.value = true;
      let slowOljTimer = null;

      try {
        slowOljTimer = setTimeout(() => {
          showToastNotification(
            "info",
            "Taking longer than usual",
            "This is taking longer than usual to respond. Please wait or try again later.",
          );
        }, 30000);

        const resumeId = userResumes.value?.[0]?.id || null;

        const fromSaved = extractPromptFromInstructions(savedAIInstructions.value);
        const fromCurrent = (currentAIInstructions.value || "").trim();
        const fromSnapshot = (lastAiGeneratePromptSnapshot.value || "").trim();
        let customPromptPayload = null;
        if (coverLetterDraftSource.value === "ai") {
          customPromptPayload = fromSnapshot || fromCurrent || (fromSaved && fromSaved.trim()) || null;
        }

        const coverLetterPayload = {
          job_post_id: jobId.value,
          resume_id: resumeId,
          content: coverLetterContent.value,
          subject: coverLetterSubject.value || `Application for ${props.job?.title || ""}`,
          custom_prompt: customPromptPayload,
          generation_source: coverLetterDraftSource.value,
        };
        if (coverLetterDraftSource.value === "ai") {
          if (lastAiSystemPromptSnapshot.value)
            coverLetterPayload.ai_system_prompt = lastAiSystemPromptSnapshot.value;
          if (lastAiUserPromptSnapshot.value)
            coverLetterPayload.ai_user_prompt = lastAiUserPromptSnapshot.value;
        }

        const coverLetterResponse = await apiClient.post("/cover-letters/", coverLetterPayload);

        showToastNotification("info", "Applying...", "Your application is being processed. Please wait.");

        const applicationPayload = {
          job_post_id: jobId.value,
          cover_letter_id: coverLetterResponse.data.id,
          apply_points: selectedApplyPoints.value,
        };

        const applicationResponse = await apiClient.post("/job-applications/apply/", applicationPayload);

        if (applicationResponse.status === 202 || applicationResponse.data?.task_id) {
          showToastNotification(
            "info",
            "Submitting...",
            applicationResponse.data?.message || "Your application is being submitted...",
          );

          await pollTaskStatus(applicationResponse.data.task_id, {
            onSuccess: (result) => {
              if (result && result.success === true) {
                finishApplySuccessUi(result.message || "Your application has been submitted successfully!", coverLetterResponse.data.id);
              } else if (result && result.error) {
                const redirectUrl = result.redirect_url || result.external_url;
                if (redirectUrl) {
                  showToastNotification(
                    "error",
                    "Manual Apply Required",
                    `${result.error} Opening the job in a new tab...`,
                  );
                  try {
                    window.open(redirectUrl, "_blank");
                  } catch (e) {
                    console.error("Failed to open redirect_url:", e);
                  }
                  return;
                }
                if (isOljReconnectError(result)) oljNeedsReconnect.value = true;
                showToastNotification("error", "Application Failed", formatApplyFailureMessage(result));
              } else {
                finishApplySuccessUi("Your application has been submitted successfully!", coverLetterResponse.data.id);
              }
            },
            onError: (errMsg) => {
              showToastNotification("error", "Application Failed", errMsg);
            },
          });
        } else {
          finishApplySuccessUi("Your application has been submitted successfully!", coverLetterResponse.data.id);
        }
      } catch (err) {
        console.error("Error submitting application:", err);
        if (isOljReconnectError(err)) oljNeedsReconnect.value = true;
        const data = err?.response?.data;
        const msg =
          (typeof data === "object" && data && (data.error || data.detail || data.message)) ||
          (typeof data === "string" ? data : null) ||
          "There was an error submitting your application.";
        showToastNotification("error", "Application Failed", msg, 8000);
      } finally {
        if (slowOljTimer) clearTimeout(slowOljTimer);
        isSubmitting.value = false;
      }
    };

    const close = () => emit("close");

    const resetState = () => {
      coverLetterContent.value = "";
      coverLetterBase.value = "";
      coverLetterDraftSource.value = "manual";
      lastAiGeneratePromptSnapshot.value = null;
      lastAiSystemPromptSnapshot.value = null;
      lastAiUserPromptSnapshot.value = null;
      showCustomizePanel.value = false;
      rewriteMessages.value = [];
      rewriteInput.value = "";
      isEditingOutput.value = false;
      editDraft.value = "";
      copiedMessageId.value = null;
      manualMode.value = false;
      letterQuestions.value = null;
      shownQuestionIds.clear();
      messageIdCounter = 0;
      oljApplyPoints.value = null;
      oljApplyPointsError.value = null;
      selectedApplyPoints.value = 0;
      placeholders.reset();
    };

    // Set the moment the panel opens, before the quota/resume/OLJ fetches that
    // decide whether auto-generate will fire — keeps the loading state showing
    // instead of flashing the manual-editor fallback while that decision is pending.
    // Once generation actually starts, the chat takes over (with its own
    // "Writing…" bubble), so the chat box is visible while the first draft is written.
    const autoGeneratePending = ref(false);
    const preparingCoverLetter = computed(
      () => autoGeneratePending.value && !generatingCoverLetter.value,
    );

    // Every generate/rewrite turn is saved server-side against (user, job_post) -
    // reopening the panel for a job the user already has a draft for replays that
    // saved conversation instead of generating (and spending a credit) again.
    const loadSavedConversation = async () => {
      try {
        const res = await apiClient.get("/cover-letters/conversation/", {
          params: { job_id: jobId.value },
        });
        const turns = Array.isArray(res.data?.turns) ? res.data.turns : [];
        if (!turns.length) return false;

        let latestSubject = "";
        let latestContent = "";
        const hydrated = turns.map((t) => {
          messageIdCounter += 1;
          if (t.role === "user") {
            return { id: messageIdCounter, role: "user", text: t.text };
          }
          latestSubject = t.subject || latestSubject;
          latestContent = t.content || "";
          return {
            id: messageIdCounter,
            role: "assistant",
            subject: t.subject,
            text: t.content,
            isError: false,
            coverLetterId: t.id ?? null,
          };
        });

        rewriteMessages.value = hydrated;
        coverLetterSubject.value = latestSubject || coverLetterSubject.value;
        coverLetterBase.value = latestContent;
        setFilledContent(withPlaceholders(latestContent));
        coverLetterDraftSource.value = "ai";

        await nextTick();
        autoResizeCoverLetterTextarea();
        scrollChatToBottom();
        return true;
      } catch (e) {
        console.error("Failed to load saved cover letter conversation:", e);
        return false;
      }
    };

    /**
     * Opening the sidebar replays a saved conversation for this job if one
     * exists; otherwise it generates the cover letter with AI right away (no
     * separate "Generate with AI" click) whenever the user is eligible -
     * logged in, on a plan that includes AI, and within quota. The job post
     * has everything the letter needs, so OLJ jobs don't wait for a connected
     * account. Otherwise it falls back to a plain editable textarea so the
     * user can write their own, same as before.
     */
    watch(
      () => [props.open, jobId.value],
      async ([isOpen], old) => {
        if (!isOpen || !jobId.value) return;
        const prevJobId = old ? old[1] : null;
        const prevOpen = old ? old[0] : false;
        if (prevOpen && prevJobId === jobId.value) return;

        resetState();
        coverLetterSubject.value = `Application for ${props.job?.title || ""}`;
        autoGeneratePending.value = true;

        try {
          await Promise.all([
            loadSavedAIInstructions(),
            fetchAiGenerateQuota(),
            fetchUserResumes(),
            fetchOljConnectionStatus(),
          ]);

          // Apply points need a connected, working OLJ login.
          if (props.job?.source === "olj" && oljConnected.value && !oljNeedsReconnect.value) fetchOljApplyPoints();

          const hasSavedConversation = authStore.state.isLoggedIn
            ? await loadSavedConversation()
            : false;

          if (!hasSavedConversation) {
            const eligibleForAutoGenerate =
              canUseAICoverLetterFeatures.value && canGenerateAiCoverLetterNow.value;
            if (eligibleForAutoGenerate) {
              const questions = await loadLetterQuestions();
              if (questions.length) {
                questions.forEach((q) => shownQuestionIds.add(q.id));
                letterQuestions.value = questions;
              } else {
                await generateCoverLetter();
              }
            }
          }
        } finally {
          autoGeneratePending.value = false;
        }
      },
      { immediate: true },
    );

    return {
      // toast
      showToast,
      toastType,
      toastTitle,
      toastMessage,

      // state
      CHAT_CHIPS,
      rememberedDetails,
      sendChatChip,
      jobId,
      checkingJob,
      letterQuestions,
      onLetterQuestionsDone,
      coverLetterSubject,
      coverLetterContent,
      coverLetterTextarea,
      coverLetterDraftSource,
      generatingCoverLetter,
      preparingCoverLetter,
      aiLimitNotice,
      isSubmitting,
      rewriteMessages,
      rewriteInput,
      rewritingCoverLetter,
      bodyScrollEl,
      isEditingOutput,
      editDraft,
      editTextarea,
      autoResizeEditTextarea,
      copiedMessageId,
      latestOutputMessageId,
      showAiChat,
      manualMode,
      regenerateCoverLetter,
      writeOwnCoverLetter,
      quotaBadgeText,
      showCustomizePanel,
      aiInstructionsCustomPresets,
      selectedTemplateIndex,
      oljConnected,
      oljNeedsReconnect,
      oljReconnectRequired,
      OLJ_RECONNECT_ROUTE,
      missingApplyPoints,
      oljApplyPoints,
      oljApplyPointsLoading,
      oljApplyPointsError,
      selectedApplyPoints,
      showApplyPoints,
      placeholderAnswers: placeholders.placeholderAnswers,
      placeholderKeys: placeholders.placeholderKeys,
      suggestingPlaceholder: placeholders.suggestingPlaceholder,
      onPlaceholderTyped: placeholders.onPlaceholderTyped,
      suggestPlaceholder: placeholders.suggestPlaceholder,

      // computed
      employerName,
      isLoggedIn,
      oljUnlocked,
      isExternalJob,
      appliesOnSite,
      externalPlatformLabel,
      showExtensionNudge,
      dismissExtensionNudge,
      extensionStoreUrl: EXTENSION_STORE_URL,
      canGenerateAiCoverLetterNow,
      generateWithAiButtonTitle,
      isFreeTrialAiLimited,
      trialAiQuotaRemaining,
      trialAiQuotaLimit,
      trialAiQuotaPeriod,
      showAiCoverLetterUpgradeHint,
      expiredPlanLabel,

      // methods
      generateCoverLetter,
      sendRewriteInstruction,
      startEditingOutput,
      saveEditingOutput,
      cancelEditingOutput,
      copyMessageText,
      submitApplication,
      openJobOnPlatform,
      autoResizeCoverLetterTextarea,
      onCoverLetterFocus,
      viewJobPost,
      close,
    };
  },
};
</script>
